import { Inline, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import Popup from '@databases/atlaskit/Popup';
import { ValidationStatusContainer } from '@databases/form/ValidationStatus';
import { InlineEditContainer } from '@databases/inline-edit/InlineEdit';
import InlineEditWrapper, {
  BorderContainer,
  InlineEditWrapperContainer,
} from '@databases/inline-edit/InlineEditWrapper';
import Z_INDEX from '@databases/layout/z-index';
import { useHoverState, useResizeObserver } from '@databases/react';
import { useHasOpenMenu } from '@databases/react/openMenu';
import { defaultStoreUtils } from '@databases/redux';
import { type Adjacents } from '@databases/redux/cellSelections/selectors';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { SkeletonItemPlaceholder } from '@databases/skeleton';
import { DatabaseFieldType } from '@databases/types/database';
import { type DatabaseViewSettings } from '@databases/types/database/view';
import { TABLE_VIEW_HARD_COLUMN_MIN_WIDTH } from '@databases/utils/constants';
import {
  getDatabaseFieldRequired,
  getExistingFieldNames,
} from '@databases/utils/database/utils';
import { ViewChangeTypes } from '@databases/utils/database/view/view';
import { isUniqueFieldName } from '@databases/utils/field-name';
import useView from '@databases/views/useView';
import composeRefs from '@seznam/compose-react-refs';
import React, { forwardRef, useEffect, useMemo, useState, useCallback } from 'react';
import styled, { css } from 'styled-components';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';
import { getExperimentValue } from '@canvas/client-feature-flags/experiments';

import FieldName from './FieldName';
import FieldNameContainer from './FieldNameContainer';
import { FieldNameEditContainerDiv } from './FieldNameEdit';
import FieldNameHeader from './FieldNameHeader';
import { FieldNameViewContainer } from './FieldNameViewContainer';
import SortingAction from './SortingAction';

const Description = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${token('space.025', '2px')};
  white-space: pre-line;
  padding: ${token('space.200', '16px')} ${token('space.300', '24px')};

  ${FieldNameContainer} {
    font: ${token('font.body.small')};
    color: ${token('color.text', '#172B4D')};
  }
`;

// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
export const FieldNameInlineContainer = styled.div<{
  hasSorting?: boolean;
  hideText?: boolean;
  isFirstInRow?: boolean;
  isLastInRow?: boolean;
  isSelected?: boolean;
  selectedAdjacentCells?: Adjacents;
  visualRefreshIcons?: boolean;
}>`
  display: flex;
  flex-grow: 1;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;

  > ${FieldNameContainer} {
    > :first-child {
      flex-shrink: 0;
      position: absolute;
      left: ${({ isFirstInRow }) => (isFirstInRow ? '20px' : '8px')};
      top: calc(50% - ${token('space.100')});
      z-index: 1;
      pointer-events: auto;
    }

    > ${InlineEditWrapperContainer}:nth-child(2) {
      margin-left: 0;
      width: 100%;
      height: 100%;
      position: absolute;
      left: 0;
      top: 0;
      justify-content: center;

      > ${ValidationStatusContainer} {
        flex-grow: 1;
        display: flex;

        ${InlineEditContainer} {
          flex-grow: 1;
          display: flex;
          justify-content: center;

          > div {
            padding-right: ${({ hasSorting, isLastInRow }) =>
              hasSorting && isLastInRow
                ? '85px'
                : isLastInRow
                ? '68px'
                : hasSorting
                ? '52px'
                : '30px'};
            justify-content: center;
          }
        }
      }

      ${FieldNameViewContainer} {
        white-space: pre;
        overflow: hidden;
        display: block;
        text-overflow: ellipsis;
        ${({ hideText }) =>
          hideText &&
          css`
            display: none;
          `}
      }

      ${FieldNameViewContainer}, ${FieldNameEditContainerDiv} > div > input, ${SkeletonItemPlaceholder} {
        margin-left: ${({ isFirstInRow, visualRefreshIcons }) =>
          isFirstInRow
            ? !visualRefreshIcons
              ? '40px'
              : '44px'
            : !visualRefreshIcons
            ? '28px'
            : '32px'} !important;
      }

      > ${BorderContainer} {
        border-bottom: none !important;
        border-bottom-left-radius: unset;
        border-bottom-right-radius: unset;
      }
    }
  }
`;

export const FieldNameWithActionsContainer = styled.div.attrs({
  id: 'field-name-with-actions-container',
  'data-interactive': true,
})<{
  isSelected?: boolean;
  isSoftSelected?: boolean;
  isIndentedMenu?: boolean;
  isVisualRefreshM1Enabled: boolean;
}>`
  display: flex;
  flex-grow: 1;
  z-index: 1;
  column-gap: 2px;

  ${({ isIndentedMenu }) =>
    isIndentedMenu &&
    css`
      > :last-child {
        margin-right: ${token('space.500', '40px')} !important;
      }
    `};

  ${({ isSelected, isSoftSelected }) =>
    isSelected &&
    !isSoftSelected &&
    css`
      background-color: ${token('color.background.selected', '#E9F2FF')};
    `};

  * {
    min-width: 0;
  }
`;

export interface FieldNameWithActionsProps {
  contentId: string;
  fieldId: string;
  isReadonly?: boolean;
  couldEditFields?: boolean;
  isDragging?: boolean;
  isSelected?: boolean;
  isSoftSelected?: boolean;
  hoverDeleteButton?: boolean;
  isFirstInRow?: boolean;
  isLastInRow?: boolean;
  selectedAdjacentCells?: Adjacents;
}

/**
 * Renders a field name along with its field actions.
 */
const FieldNameWithActions = forwardRef<HTMLInputElement, FieldNameWithActionsProps>(
  function FieldNameWithActions(
    {
      contentId,
      fieldId,
      isReadonly,
      couldEditFields,
      isDragging = false,
      isSelected,
      isSoftSelected,
      hoverDeleteButton,
      isFirstInRow,
      isLastInRow,
      selectedAdjacentCells,
    },
    inputRef
  ) {
    const { view, setViewChange } = useView();
    const [isHover, hoverProps] = useHoverState();
    const hasOpenMenu = useHasOpenMenu();
    const [fieldNameWithActionsContainer, setFieldNameWithActionsContainer] =
      useState<HTMLDivElement | null>(null);
    const width = useResizeObserver(fieldNameWithActionsContainer)?.width;

    const [hasSentFieldAnalytics, setHasSentFieldAnalytics] = useState(false);
    const client = useAnalytics();

    const isVisualRefreshM1Enabled = isFeatureFlagEnabled(
      'databases-frontend-visual-refresh-table-m1'
    );

    const [field, isInvalid, fieldHasValues] = defaultStoreUtils.useEqualSelector(
      (state) => {
        const databaseDraft = selectDatabaseDraftByContentId(state, {
          contentId,
          includeDeleted: true,
        });
        const field = getDatabaseFieldRequired(databaseDraft, fieldId);
        const fieldHasValues = Object.values(databaseDraft.entriesById).some(
          (entry) => fieldId in entry.values
        );

        const isInvalid = !isUniqueFieldName(
          field.name,
          getExistingFieldNames(databaseDraft, field)
        );

        return [field, isInvalid, fieldHasValues] as const;
      }
    );

    const containerType = getDatabaseContainerType();

    if (
      !hasSentFieldAnalytics &&
      field.type === DatabaseFieldType.ENTRY_LINK &&
      fieldHasValues &&
      !field.settings.databaseId &&
      !field.settings.contentId
    ) {
      client.sendEntryLinkFieldAnalytics({
        contentId,
        source: 'fieldNameWithActions',
        fieldId: field.id,
        databaseContainer: containerType,
      });
      setHasSentFieldAnalytics(true);
    }

    useEffect(() => {
      if (!field.type || field.type !== DatabaseFieldType.JIRA_ISSUE) {
        return;
      }
      const serverId = field.settings.jiraServerId;
      const cloudId = field.settings.cloudId;
      if (serverId && !cloudId) {
        client.sendDatasourcesDBMigrationConvertIdFailedEvent({
          source: 'FieldNameWithActions',
          missingField: 'cloudId',
          serverId,
          contentId,
          databaseContainer: containerType,
        });
      }
      if (!serverId && cloudId) {
        client.sendDatasourcesDBMigrationConvertIdFailedEvent({
          source: 'FieldNameWithActions',
          missingField: 'serverId',
          cloudId,
          contentId,
          databaseContainer: containerType,
        });
      }
    }, [
      client,
      containerType,
      contentId,
      field.settings.cloudId,
      field.settings.jiraServerId,
      field.type,
    ]);

    const description = useMemo(
      () => (
        <Description data-testid="field-name-with-actions-description">
          <FieldName field={field} isBold />
          <div>{field.description}</div>
        </Description>
      ),
      [field]
    );

    const handleSortChange = useCallback(
      (sort: DatabaseViewSettings['sorts'][number]) => {
        setViewChange({
          type: ViewChangeTypes.VIEW_SORT_UPDATE_VALUE,
          fieldId: sort.fieldId,
          ascending: sort.ascending,
        });
      },
      [setViewChange]
    );

    const sort = useMemo(() => {
      return view.settings.sorts.find((sort) => sort.fieldId === field.id);
    }, [field.id, view.settings.sorts]);
    const isActiveSortField = sort !== undefined;

    // if showDescription is undefined, its a database that was made before this feature
    // so they haven't had a chance to toggle it true or false. But we can assume that if
    // there's a field description and showDescription is undefined, then they want it to show
    const showFieldDescription =
      !!field?.description &&
      (field.showDescription || field.showDescription === undefined);

    const shouldHideName = (width || 0) < TABLE_VIEW_HARD_COLUMN_MIN_WIDTH + 20;

    const isCheckboxEnabled = getExperimentValue('checkboxFieldType', 'enabled', {
      fireExperimentExposure: false,
    });
    // if checkbox ff is off, disable field setting
    if (!isCheckboxEnabled && field.type === DatabaseFieldType.CHECKBOX) {
      isReadonly = true;
    }

    const visualRefreshIcons = isFeatureFlagEnabled('platform-visual-refresh-icons');

    const actionComp = (
      <Inline
        alignBlock="center"
        alignInline="center"
        xcss={xcss({
          paddingRight: 'space.150',
          marginRight: isLastInRow ? 'space.500' : 'space.0',
        })}
      >
        {isActiveSortField && (
          <SortingAction
            ascending={sort.ascending}
            field={field}
            onChange={handleSortChange}
            contentId={contentId}
          />
        )}
      </Inline>
    );
    return (
      <InlineEditWrapper isInvalid={isInvalid}>
        <Popup
          isOpen={showFieldDescription && !hasOpenMenu && !isDragging && isHover}
          content={() => description}
          placement="bottom"
          trigger={(triggerProps) => (
            <FieldNameWithActionsContainer
              isSelected={isSelected}
              isSoftSelected={isSoftSelected}
              isIndentedMenu={isLastInRow && couldEditFields}
              {...hoverProps}
              {...triggerProps}
              ref={composeRefs(triggerProps.ref, setFieldNameWithActionsContainer)}
              isVisualRefreshM1Enabled={isVisualRefreshM1Enabled}
            >
              <FieldNameInlineContainer
                hasSorting={isActiveSortField}
                hideText={shouldHideName}
                isFirstInRow={isFirstInRow}
                isLastInRow={isLastInRow}
                isSelected={isSelected}
                selectedAdjacentCells={selectedAdjacentCells}
                visualRefreshIcons={visualRefreshIcons}
              >
                <FieldNameHeader
                  field={field}
                  showIcon
                  contentId={contentId}
                  isSelected={isSelected}
                  hoverDeleteButton={hoverDeleteButton}
                  isReadonly={isReadonly}
                  selectedAdjacentCells={selectedAdjacentCells}
                  ref={inputRef}
                  actionComp={!isReadonly && actionComp}
                  hideText={shouldHideName}
                />
              </FieldNameInlineContainer>
            </FieldNameWithActionsContainer>
          )}
          zIndex={Z_INDEX.TOOLTIP}
          isTooltip
        />
      </InlineEditWrapper>
    );
  }
);

export default FieldNameWithActions;
